import React from "react";
import {
  ADDITIONAL_COMMON_MODEL_INFO,
  DeprecationReplacement,
  DeprecationStage,
} from "../../../../../../constants";
import { APICategory } from "../../../../../../models/Entities";

/**
 * Deprecation lookups for the scopes page. Deprecation is not on the API model; it lives in the
 * hardcoded ADDITIONAL_COMMON_MODEL_INFO constant keyed by category -> model ->
 * { deprecationStage, deprecationMessage, fields }. SUNSET items are hidden unless the org opts in,
 * DEPRECATED items surface their message and are hidden once disabled (see isHidden).
 */

export interface DeprecationInfo {
  deprecationStage?: DeprecationStage;
  deprecationMessage?: React.ReactNode;
  deprecationDate?: string;
  deprecationReplacement?: DeprecationReplacement;
}

// Model-level deprecation (e.g. HRIS Team, Ticketing Project).
export const getModelDeprecation = (category: APICategory, modelName: string): DeprecationInfo => {
  const model = ADDITIONAL_COMMON_MODEL_INFO[category]?.[modelName];
  return {
    deprecationStage: model?.deprecationStage,
    deprecationMessage: model?.deprecationMessage,
    deprecationDate: model?.deprecationDate,
    deprecationReplacement: model?.deprecationReplacement,
  };
};

// Field-level deprecation (e.g. Benefit.benefit_plan_type, *.tracking_category).
export const getFieldDeprecation = (
  category: APICategory,
  modelName: string,
  fieldName: string,
): DeprecationInfo => {
  const field = ADDITIONAL_COMMON_MODEL_INFO[category]?.[modelName]?.fields?.[fieldName];
  return {
    deprecationStage: field?.deprecationStage,
    deprecationMessage: field?.deprecationMessage,
    deprecationDate: field?.deprecationDate,
    deprecationReplacement: field?.deprecationReplacement,
  };
};

/**
 * Whether a deprecated item is hidden from the table, given the org's escape-hatch flag
 * (MERGE_FLAG_SHOW_DEPRECATED_SUNSET_ITEMS; when on, nothing is hidden):
 *   - SUNSET: always hidden (it's leaving the API), enabled or not.
 *   - DEPRECATED: hidden only once turned off, so an enabled deprecated item you still rely on
 *     stays visible while one you've disabled drops out of the list.
 * `isEnabled` is read from the SAVED state, not the in-flight edit, so disabling something in edit
 * mode doesn't make its row vanish under the cursor; it drops out only after the save persists.
 */
const isHidden = (
  stage: DeprecationStage | undefined,
  isEnabled: boolean,
  showDeprecatedSunsetItems: boolean,
): boolean => {
  if (showDeprecatedSunsetItems || !stage) return false;
  if (stage === DeprecationStage.SUNSET) return true;
  return stage === DeprecationStage.DEPRECATED && !isEnabled;
};

// A deprecated model is hidden when sunset, or when deprecated-and-disabled (unless the flag is on).
export const isModelHidden = (
  category: APICategory,
  modelName: string,
  isEnabled: boolean,
  showDeprecatedSunsetItems: boolean,
): boolean =>
  isHidden(
    getModelDeprecation(category, modelName).deprecationStage,
    isEnabled,
    showDeprecatedSunsetItems,
  );

// Hidden by the field's OWN deprecation only — a field that merely inherits its model's deprecation
// is never hidden by its toggle (see ScopeObjectRow), so an enabled deprecated model shows all its
// fields. Sunset fields stay hidden regardless; deprecated fields hide once disabled.
export const isFieldHidden = (
  category: APICategory,
  modelName: string,
  fieldName: string,
  isEnabled: boolean,
  showDeprecatedSunsetItems: boolean,
): boolean =>
  isHidden(
    getFieldDeprecation(category, modelName, fieldName).deprecationStage,
    isEnabled,
    showDeprecatedSunsetItems,
  );
